/**
 * Canvas-level display settings shared with node components.
 *
 * Node components are rendered deep inside Svelte Flow's tree, so passing
 * board-wide toggles through each node's `data` would mean rewriting every node
 * whenever a toggle flips. Context lets them read the live value instead.
 */
import { getContext, setContext } from 'svelte';
import type { ResizeParams } from '@xyflow/svelte';
import type { Task } from './types';

const BOARD_DISPLAY_KEY = Symbol('board-display');
const BOARD_ACTIONS_KEY = Symbol('board-actions');
const BOARD_TASK_UI_KEY = Symbol('board-task-ui');

export interface BoardDisplay {
  showCompleted: boolean;
  /** Current canvas zoom, used by cards to switch to a readable summary mode. */
  zoom: number;
}

export function setBoardDisplay(display: BoardDisplay): void {
  setContext(BOARD_DISPLAY_KEY, display);
}

export function getBoardDisplay(): BoardDisplay {
  return getContext<BoardDisplay>(BOARD_DISPLAY_KEY) ?? { showCompleted: false, zoom: 1 };
}

/**
 * Persistence callbacks node components invoke.
 *
 * These deliberately live in context rather than in each node's `data`: Svelte
 * Flow detects accidentally-deep-reactive node arrays by structured-cloning the
 * first node, and functions stored in `data` make that clone throw, producing a
 * spurious "use $state.raw" warning on every board load.
 */
export interface BoardActions {
  onResizeStart?: (id: string) => void;
  onResizeEnd: (id: string, params: ResizeParams) => void;
  onContentStart?: (id: string) => void;
  onContentChange: (id: string, content: string) => void;
  onDelete: (id: string) => void;
}

export function setBoardActions(actions: BoardActions): void {
  setContext(BOARD_ACTIONS_KEY, actions);
}

export function getBoardActions(): BoardActions {
  return (
    getContext<BoardActions>(BOARD_ACTIONS_KEY) ?? {
      onResizeStart: () => {},
      onResizeEnd: () => {},
      onContentStart: () => {},
      onContentChange: () => {},
      onDelete: () => {},
    }
  );
}

/**
 * Task overlays (the "⋯" actions menu and the task detail popover) opened from a
 * card but rendered by the canvas.
 *
 * They cannot live inside the node: Svelte Flow applies a CSS transform to the
 * viewport, which makes `position: fixed` resolve against that transformed
 * ancestor instead of the window, and the card's own `overflow` would clip them.
 * Cards therefore report "open this, anchored here" and the canvas renders the
 * overlay outside the flow, where Floating UI can position it against the live
 * anchor rect.
 */
export interface BoardTaskUi {
  /** Task id whose actions menu is currently open, for row highlighting. */
  readonly openMenuTaskId: string | null;
  toggleMenu(task: Task, projectId: string, anchorEl: HTMLElement): void;
  openDetail(task: Task, projectId: string, anchorEl: HTMLElement): void;
}

export function setBoardTaskUi(ui: BoardTaskUi): void {
  setContext(BOARD_TASK_UI_KEY, ui);
}

export function getBoardTaskUi(): BoardTaskUi {
  return (
    getContext<BoardTaskUi>(BOARD_TASK_UI_KEY) ?? {
      openMenuTaskId: null,
      toggleMenu: () => {},
      openDetail: () => {},
    }
  );
}
